import { useCallback, useMemo } from 'react';
import type { ValueLists } from '@ohpen/enso-ui';
import { FirmGetAllResponse, firmsSqe, type FirmsSqeRequest } from '@pmp/api';
import { useSqeList } from '@pmp/hooks';
import type { SqePage, SqeQuery } from '@pmp/utils';

import { columns, firmSqeDefaultSort, firmSqeFields } from './tableConfig';
import { OPERATION_ORDER } from './utils';
import { FirmExportRow, toExportRows } from './exportRows';

const EXPORT_ROWS = Symbol('exportRows');

type FirmListRow = FirmGetAllResponse & { [EXPORT_ROWS]: FirmExportRow[] };

// The shape the list columns render: the principal as the `{ key, value }` pair a value list reads, and operations
// in their display order. It carries the rows the firm exports as, which the list response alone gives.
const toListRow = (response: FirmGetAllResponse): FirmGetAllResponse => {
  const firm = {
    ...response,
    operationsWhitelist: [...response.operationsWhitelist].sort(
      (a, b) => OPERATION_ORDER.indexOf(a.key ?? '') - OPERATION_ORDER.indexOf(b.key ?? ''),
    ),
  };
  const listRow = {
    ...firm,
    principalFirm: firm.principalFirm ? { key: firm.principalFirm.id, value: firm.principalFirm.name ?? '' } : null,
  };
  return { ...listRow, [EXPORT_ROWS]: toExportRows(firm, listRow) } as unknown as FirmGetAllResponse;
};

/** The rows the given list rows export as: one per trading address, in the shape the firm import reads. */
export const firmExportRows = (rows: readonly unknown[]): FirmExportRow[] => rows.flatMap(row => (row as FirmListRow)[EXPORT_ROWS] ?? []);

const fetchFirmsPage = async (query: SqeQuery): Promise<SqePage<FirmGetAllResponse>> => {
  const { data } = await firmsSqe({ body: query as FirmsSqeRequest });
  return { items: data.items.map(toListRow), total: data.total };
};

export const useFirmsSqe = (valueLists?: ValueLists) => {
  const list = useSqeList(fetchFirmsPage, columns, firmSqeFields, valueLists, firmSqeDefaultSort);
  const { exportFetchAll: fetchListRows } = list;

  const exportFetchAll = useCallback(async (scope: 'view' | 'all') => firmExportRows(await fetchListRows(scope)), [fetchListRows]);

  return useMemo(() => ({ ...list, exportFetchAll }), [list, exportFetchAll]);
};
